import { createContext, useContext, useState, ReactNode } from 'react';
import { fetchFavoriteItems } from '../Services/AppAction';
import { getData } from '../Services/Util';

interface FavoriteContextType {
  favCount: number;
  setFavCount: React.Dispatch<React.SetStateAction<number>>;
  incrementFavCount: () => void;
  decrementFavCount: () => void;
  clearFavCount: () => void;
  fetchAndUpdateFavCount: () => Promise<void>;
}

const FavoriteContext = createContext<FavoriteContextType | undefined>(undefined);

interface FavoriteProviderProps {
  children: ReactNode;
}

export const FavoriteProvider = ({ children }: FavoriteProviderProps) => {
  const [favCount, setFavCount] = useState<number>(0);

  const incrementFavCount = () => setFavCount(prev => prev + 1);
  const decrementFavCount = () => setFavCount(prev => (prev > 0 ? prev - 1 : 0));
  const clearFavCount = () => setFavCount(0);

  const fetchAndUpdateFavCount = async () => {
    try {
      const userData = await getData('userData');
      const partyId = userData?.PARTY_ID;
      if (!partyId) {return;}

      const response = await fetchFavoriteItems(partyId);
      if (Array.isArray(response?.data)) {
        setFavCount(response.data.length);
      }
    } catch (err) {
      console.error('Error fetching favorite count:', err);
    }
  };

  return (
    <FavoriteContext.Provider
      value={{
        favCount,
        setFavCount,
        incrementFavCount,
        decrementFavCount,
        clearFavCount,
        fetchAndUpdateFavCount,
      }}
    >
      {children}
    </FavoriteContext.Provider>
  );
};

export const useFavorite = (): FavoriteContextType => {
  const context = useContext(FavoriteContext);
  if (!context) {throw new Error('useFavorite must be used within a FavoriteProvider');}
  return context;
};
